/**
 * TestDemoPage
 *
 * Accessible from the sidebar "Test" menu item.
 * Allows operators to select an agent and generate/manage a public demo link,
 * then test the chat directly from the console.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useAgentSelector } from '../../auth/AgentSelectorContext';
import { Card, Button, Tooltip } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { DemoMessageLimitsSection } from './DemoMessageLimitsSection';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Agent {
  id: string;
  name: string;
  dailyMessageLimit?: number | null;
  extensionAmount?: number | null;
  extensionValidityDays?: number | null;
}

interface DemoLink {
  id: string;
  virtualAgentId: string;
  token: string;
  isEnabled: boolean;
  config: {
    agentName: string;
    primaryColor: string;
    accentColor: string;
    welcomeMessage: string;
    logoUrl?: string;
    showPoweredBy: boolean;
    theme: 'light' | 'dark' | 'system';
  };
  maxMessagesPerMinute: number;
  maxConversationLength: number;
  expiresAt: string | null;
  visitCount: number;
  conversationCount: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconCopy() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconExternalLink() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="14" x2="21" y2="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="23 4 23 10 17 10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const demoUrlIconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: tokens.borderRadius.md,
  border: `1px solid ${tokens.colors.neutral[300]}`,
  backgroundColor: tokens.colors.neutral[50],
  color: tokens.colors.neutral[700],
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background-color 0.15s, border-color 0.15s',
};

// ─── Component ───────────────────────────────────────────────────────────────

export function TestDemoPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY);
  const { selectedAgentId, selectAgent } = useAgentSelector();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [demoLink, setDemoLink] = useState<DemoLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Message limit state
  const [messageLimit, setMessageLimit] = useState<number | null>(null);
  const [validityDays, setValidityDays] = useState<number | null>(null);
  const [extensionAmount, setExtensionAmount] = useState<number | null>(null);
  const [limitErrors, setLimitErrors] = useState<Record<string, string>>({});
  const [limitSaving, setLimitSaving] = useState(false);
  const [limitSaved, setLimitSaved] = useState('');

  // Load agents (detalle con límites; la selección viene del selector del sidebar)
  const loadAgents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await apiGet<Agent[]>('/api/console/agents', token);
      setAgents(data);
      if (data.length > 0 && !selectedAgentId) {
        selectAgent(data[0]!.id);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, selectedAgentId, selectAgent]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  // Load demo links for selected agent
  const loadDemoLink = useCallback(async () => {
    if (!token || !selectedAgentId) return;
    try {
      const data = await apiGet<DemoLink[]>(
        `/api/agents/${selectedAgentId}/demo`,
        token
      );
      // Show the most recent one
      setDemoLink(data && data.length > 0 ? data[0] : null);
    } catch {
      setDemoLink(null);
    }
  }, [token, selectedAgentId]);

  useEffect(() => {
    void loadDemoLink();
  }, [loadDemoLink]);

  // Sync limit state when selected agent changes
  useEffect(() => {
    const agent = agents.find((a) => a.id === selectedAgentId);
    if (agent) {
      setMessageLimit(agent.dailyMessageLimit ?? null);
      setValidityDays(agent.extensionValidityDays ?? null);
      setExtensionAmount(agent.extensionAmount ?? null);
      setLimitErrors({});
      setLimitSaved('');
    }
  }, [selectedAgentId, agents]);

  function handleLimitChange(field: string, value: number | null) {
    const ranges: Record<string, { min: number; max: number }> = {
      messageLimit: { min: 1, max: 10000 },
      validityDays: { min: 1, max: 365 },
      extensionAmount: { min: 1, max: 10000 },
    };

    if (field === 'messageLimit') setMessageLimit(value);
    else if (field === 'validityDays') setValidityDays(value);
    else if (field === 'extensionAmount') setExtensionAmount(value);

    setLimitErrors((prev) => {
      const next = { ...prev };
      if (value === null) {
        delete next[field];
      } else {
        const range = ranges[field];
        if (range && (!Number.isInteger(value) || value < range.min || value > range.max)) {
          next[field] = `Debe ser un entero entre ${range.min} y ${range.max}`;
        } else {
          delete next[field];
        }
      }
      return next;
    });
    setLimitSaved('');
  }

  const saveLimits = async () => {
    if (!token || !selectedAgentId || Object.keys(limitErrors).length > 0) return;
    setLimitSaving(true);
    setError('');
    setLimitSaved('');
    try {
      await apiPatch(
        `/api/console/agents/${selectedAgentId}`,
        {
          dailyMessageLimit: messageLimit,
          extensionAmount,
          extensionValidityDays: validityDays,
        },
        token,
        { successMessage: 'Límites guardados' }
      );
      setLimitSaved('Límites guardados correctamente.');
      // Update the local agent data
      setAgents((prev) =>
        prev.map((a) =>
          a.id === selectedAgentId
            ? { ...a, dailyMessageLimit: messageLimit, extensionAmount, extensionValidityDays: validityDays }
            : a
        )
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLimitSaving(false);
    }
  };

  // Generate demo link
  const handleGenerate = async () => {
    if (!token || !selectedAgentId) return;
    setGenerating(true);
    setError('');
    try {
      const agent = agents.find(a => a.id === selectedAgentId);
      const payload = {
        config: {
          agentName: agent?.name || 'Agente',
          primaryColor: '#7c3aed',
          accentColor: '#06b6d4',
          welcomeMessage: `¡Hola! Soy ${agent?.name || 'tu asistente'}. ¿En qué puedo ayudarte?`,
          showPoweredBy: true,
          theme: 'system',
        },
      };
      const created = await apiPost<DemoLink>(
        `/api/agents/${selectedAgentId}/demo`,
        payload,
        token,
        { successMessage: 'Link de demo generado' }
      );
      setDemoLink(created);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  // Copy URL
  const getDemoUrl = () => {
    if (!demoLink) return '';
    return `${window.location.origin}/demo/${demoLink.token}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getDemoUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  // Open in new tab
  const handleOpenDemo = () => {
    window.open(getDemoUrl(), '_blank');
  };

  if (!token) return null;

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a' }}>
            Test chat
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
            Probá las respuestas del agente en un entorno de prueba.
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          Cargando agentes...
        </div>
      )}

      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Agent selector */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Seleccionar Agente
            </h2>
            {agents.length === 0 ? (
              <p style={{ margin: 0, color: tokens.colors.neutral[500], fontSize: 14 }}>
                No hay agentes creados. Creá uno desde la sección "Agentes virtuales".
              </p>
            ) : (
              <select
                value={selectedAgentId ?? ''}
                onChange={(e) => selectAgent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 15,
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${tokens.colors.neutral[300]}`,
                  backgroundColor: tokens.colors.neutral[50],
                  color: tokens.colors.neutral[900],
                  outline: 'none',
                }}
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}
          </Card>

          {/* Demo link section */}
          {selectedAgentId && (
            <Card variant="outlined" padding="lg">
              <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
                Link de Demo
              </h2>

              {!demoLink ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <p style={{ margin: '0 0 16px', color: tokens.colors.neutral[500], fontSize: 14 }}>
                    No hay un link de demo para este agente. Generá uno para compartir y probar el chatbot.
                  </p>
                  <Button variant="primary" size="md" onClick={handleGenerate} disabled={generating}>
                    {generating ? 'Generando...' : 'Generar Link de Demo'}
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* URL + icon actions (tooltips) */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 8,
                      padding: '10px 12px',
                      borderRadius: tokens.borderRadius.md,
                      backgroundColor: tokens.colors.neutral[50],
                      border: `1px solid ${tokens.colors.neutral[200]}`,
                    }}
                  >
                    <code
                      style={{
                        flex: 1,
                        minWidth: 0,
                        alignSelf: 'center',
                        fontSize: 13,
                        color: tokens.colors.neutral[800],
                        wordBreak: 'break-all',
                      }}
                    >
                      {getDemoUrl()}
                    </code>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      <Tooltip content="Abrir la demo en una pestaña nueva" position="top" showDelay={120}>
                        <button
                          type="button"
                          onClick={handleOpenDemo}
                          aria-label="Abrir demo"
                          style={demoUrlIconBtnStyle}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = tokens.colors.neutral[100];
                            e.currentTarget.style.borderColor = tokens.colors.neutral[400];
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
                            e.currentTarget.style.borderColor = tokens.colors.neutral[300];
                          }}
                        >
                          <IconExternalLink />
                        </button>
                      </Tooltip>
                      <Tooltip
                        content={
                          generating ?
                            'Generando nuevo enlace…'
                          : 'Regenerar URL: nuevo token (el enlace anterior dejará de funcionar)'
                        }
                        position="top"
                        showDelay={120}
                        disabled={generating}
                      >
                        <button
                          type="button"
                          onClick={() => void handleGenerate()}
                          disabled={generating}
                          aria-label="Regenerar URL de demo"
                          style={{
                            ...demoUrlIconBtnStyle,
                            opacity: generating ? 0.55 : 1,
                            cursor: generating ? 'not-allowed' : 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            if (generating) return;
                            e.currentTarget.style.backgroundColor = tokens.colors.neutral[100];
                            e.currentTarget.style.borderColor = tokens.colors.neutral[400];
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
                            e.currentTarget.style.borderColor = tokens.colors.neutral[300];
                          }}
                        >
                          <IconRefresh />
                        </button>
                      </Tooltip>
                      <Tooltip
                        content={copied ? 'Copiado al portapapeles' : 'Copiar enlace al portapapeles'}
                        position="top"
                        showDelay={120}
                      >
                        <button
                          type="button"
                          onClick={() => void handleCopy()}
                          aria-label="Copiar enlace"
                          style={{
                            ...demoUrlIconBtnStyle,
                            color: copied ? tokens.colors.success.dark : tokens.colors.neutral[700],
                            borderColor: copied ? tokens.colors.success.main : tokens.colors.neutral[300],
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = tokens.colors.neutral[100];
                            e.currentTarget.style.borderColor = tokens.colors.neutral[400];
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
                            e.currentTarget.style.borderColor = copied
                              ? tokens.colors.success.main
                              : tokens.colors.neutral[300];
                          }}
                        >
                          {copied ? <IconCheck /> : <IconCopy />}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Message limits section */}
          {selectedAgentId && demoLink && (
            <Card variant="outlined" padding="lg">
              <DemoMessageLimitsSection
                messageLimit={messageLimit}
                validityDays={validityDays}
                extensionAmount={extensionAmount}
                onChange={handleLimitChange}
                errors={limitErrors}
              />
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => void saveLimits()}
                  disabled={limitSaving || Object.keys(limitErrors).length > 0}
                >
                  {limitSaving ? 'Guardando...' : 'Guardar Límites'}
                </Button>
                {limitSaved && (
                  <span style={{ fontSize: 13, color: tokens.colors.success.dark }}>{limitSaved}</span>
                )}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default TestDemoPage;
